import { Alert, Avatar, Button, Result, Space, Tag, Tooltip } from 'antd';
import { EditOutlined, SwapOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { imageUrl } from 'pages/star-shared/utils';
import { initials } from 'utils/initials';
import type { StudioSummary } from '../contract';
import { describeStudioError } from '../studio-error';
import { MANAGER_INVALID_TAG, MANAGER_INVALID_TEXT, NO_MANAGER_TEXT } from '../labels';
import { displayName } from '../format';
import styles from '../styles.module.scss';

export interface StudioLoadErrorProps {
  /** Lỗi của lần tải (dạng { statusCode, message, error? } của utils/axios-utils). */
  error: any;
  onRetry?: () => void;
  /** Nút quay về ở trang 403 / 404: Quản lý Studio về /studio, người khác về danh sách hoặc Tổng quan. */
  home?: { to: string; label: string };
}

/**
 * Lỗi tải dữ liệu của trang / tab Chi tiết Studio theo bảng trạng thái mục 8.7: 503 → Result warning;
 * STUDIO_SCOPE / STUDIO_NOT_MANAGER / STUDIO_ROLE_NOT_ALLOWED → Result 403; STUDIO_NOT_FOUND → Result 404;
 * còn lại → Alert error + nút "Thử lại".
 */
export const StudioLoadError = ({ error, onRetry, home }: StudioLoadErrorProps) => {
  const view = describeStudioError(error);
  const back = home ? (
    <Link to={home.to}>
      <Button type="primary">{home.label}</Button>
    </Link>
  ) : undefined;
  if (view.kind === 'unavailable') return <Result status="warning" title={view.message} />;
  if (view.kind === 'forbidden') {
    return (
      <Result status="403" title="Không mở được Studio này" subTitle={view.message} extra={back} />
    );
  }
  if (view.code === 'STUDIO_NOT_FOUND') {
    return <Result status="404" title={view.message} extra={back} />;
  }
  return (
    <Alert
      type="error"
      showIcon
      message={view.message}
      action={
        onRetry ? (
          <Button size="small" onClick={onRetry}>
            Thử lại
          </Button>
        ) : undefined
      }
    />
  );
};

export interface StudioHeaderProps {
  studio: StudioSummary;
  /** ADMIN: hiện nút Sửa, Đổi Quản lý (mục 8.7.3). */
  canAdmin?: boolean;
  onEdit?: () => void;
  onChangeManager?: () => void;
}

/**
 * Đầu trang Chi tiết Studio (SPEC Studio mục 8.7.3). Không dùng PageTitle (tiêu đề tĩnh từ nav-config).
 * Nền `lem.nhat`; chữ trên nền này chỉ dùng heading / inkSoft / accentDeep (styles .hero*).
 */
const StudioHeader = ({ studio, canAdmin, onEdit, onChangeManager }: StudioHeaderProps) => {
  const manager = studio.manager;
  const invalidText =
    manager && !manager.valid && manager.invalidReason
      ? MANAGER_INVALID_TEXT[manager.invalidReason]
      : null;
  const assistants = (studio.assistants || []).map((a) => displayName(a)).filter(Boolean);

  return (
    <section className={styles.hero} aria-label={`Studio ${studio.title}`}>
      <div className={styles.heroMain}>
        <Avatar size={56} src={imageUrl(studio.avatar) || undefined} style={{ flex: 'none' }}>
          {initials(studio.title)}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <h1 className={styles.heroTitle}>{studio.title}</h1>
          <div className={styles.heroSub}>
            Quản lý: {manager ? <strong>{displayName(manager)}</strong> : NO_MANAGER_TEXT}
            {manager && !manager.valid && (
              <>
                {' '}
                <Tooltip title={invalidText || undefined}>
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    {MANAGER_INVALID_TAG}
                  </Tag>
                </Tooltip>
              </>
            )}
          </div>
          {assistants.length > 0 && (
            <div className={styles.heroSub}>Trợ lý: {assistants.join(', ')}</div>
          )}
          <div className={styles.heroSub}>
            Nhóm trong file TikTok:{' '}
            {studio.tiktokGroupName ? <strong>{studio.tiktokGroupName}</strong> : 'Chưa gắn'}
          </div>
        </div>
      </div>
      {canAdmin && (
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={onEdit} disabled={!onEdit}>
            Sửa
          </Button>
          <Button icon={<SwapOutlined />} onClick={onChangeManager} disabled={!onChangeManager}>
            Đổi Quản lý
          </Button>
        </Space>
      )}
    </section>
  );
};

export default StudioHeader;
